<script>
	import { onMount } from "svelte";
	import { getApi, postApi, putApi, delApi } from "../../service/api";
	import { ErrorCodes, getErrorMessage } from "../../define/define";
	import { Dates } from "../../utils/date";
	import TableToolbar from "../../components/TableToolbar.svelte";
	import Pagination from "../../components/Pagination.svelte";
	import { menu, menuSub, menuSubSub } from "../../stores";
	import { fade } from "svelte/transition";
	import SendMailModal from "../email/SendMailModal.svelte";

	// 상수 정의
	const BASE_URL = "/user/management";
	const PAGE_SIZES = [10, 15, 20, 25, 30, 50, 100];

	// 상태 관리
	let pageSize = 10;
	let currentPage = 1;
	let totalPages = 1;
	let totalItems = 0;
	let filteredData = [];
	let searchKeyword = "";
	let isLoading = false;

	// 모달 상태
	let showDetailModal = false;
	let selectedUser = null;
	let showRegistModal = false;

	//메일 발송 모달 상태
	let showMailModal = false;
	let receiver = [''];

	// 검색 옵션
	const searchOptions = [
		{ value: "ALL", text: "전체" },
		{ value: "email", text: "이메일" },
	];
	let searchType = "ALL";

	// 등록 아이템 초기값
	const defaultEditItem = {
		email: "",
		password: "",
	};
	let editItem = { ...defaultEditItem };

	// 사용자 목록 조회
	const fetchUsers = async () => {
		isLoading = true;
		try {
			const keyword = searchKeyword.trim() || "ALL";
			const url = `${BASE_URL}/${pageSize}/${currentPage}/${encodeURIComponent(keyword)}`;
			const result = await getApi(url);

			if (result.resultCode === ErrorCodes.OK) {
				filteredData = result.data.list || [];
				totalItems = result.data.total || 0;
				totalPages = Math.ceil(totalItems / pageSize);
			} else {
				alert(getErrorMessage(result.resultCode));
				filteredData = [];
				totalItems = 0;
				totalPages = 1;
			}
		} catch (error) {
			console.error("사용자 목록 조회 실패:", error);
			alert("사용자 목록을 불러오는데 실패했습니다.");
			filteredData = [];
			totalItems = 0;
			totalPages = 1;
		} finally {
			isLoading = false;
		}
	};

	// 사용자 상세 조회
	const fetchUserDetail = async (userId) => {
		try {
			const result = await getApi(`${BASE_URL}/${userId}`);
			if (result.resultCode === ErrorCodes.OK) {
				selectedUser = result.data;
				showDetailModal = true;
			} else {
				alert(getErrorMessage(result.resultCode));
			}
		} catch (error) {
			console.error("사용자 상세 조회 실패:", error);
			alert("사용자 정보를 불러오는데 실패했습니다.");
		}
	};

	const setFocus = (field = "modalEmail") => {
		const element = document.getElementById(field);
		if (element) {
			element.focus();
		}
	};
	const validateItem = () => {
		if (!editItem.email) {
			alert("이메일을 입력하세요.");
			setFocus("modalEmail");
			return false;
		}
		if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(editItem.email)) {
			alert("올바르지 않은 이메일 형식입니다.");
			setFocus("modalEmail");
			return false;
		}
		if (!editItem.password) {
			alert("PW를 입력하세요.");
			setFocus("modalPassword");
			return false;
		}
		if (editItem.password.length < 8) {
			alert("PW는 8자리 이상이여야 합니다.");
			setFocus("modalPassword");
			return false;
		}
		if (!/[a-z]/.test(editItem.password)) {
			alert("PW는 하나 이상의 영소문자를 포함해야 합니다.");
			setFocus("modalPassword");
			return false;
		}
		if (!/[0-9]/.test(editItem.password)) {
			alert("PW는 하나 이상의 숫자를 포함해야 합니다.");
			setFocus("modalPassword");
			return false;
		}
		if (!/[!@#\$&*~]/.test(editItem.password)) {
			alert("PW는 하나 이상의 특수문자를 포함해야 합니다.");
			setFocus("modalPassword");
			return false;
		}
		return true;
	};

	// 사용자 등록
	const fnProcessRegist = () => {
		editItem = { ...defaultEditItem };
		showRegistModal = true;
	};

	// 사용자 등록 모달 닫기
	const closeRegistModal = () => {
		showRegistModal = false;
		editItem = null;
	};

	// 사용자 등록 처리 함수
	const handleSave = async () => {
		if (!validateItem()) {
			console.log("유효성 검사 실패");
			return;
		}

		try {
			isLoading = true; // 로딩 시작
			let result;
			const payload = {
				...editItem,
			};
			result = await postApi(BASE_URL, payload);
			if (result.resultCode !== ErrorCodes.OK) {
				alert(result.resultDescKo);
			} else {
				closeRegistModal();
				await fetchUsers();
			}
		} catch (error) {
			console.log(error);
			alert("사용자 등록 중 오류가 발생했습니다.");
		} finally {
			isLoading = false; // 로딩 종료
		}
	};

	// 검색 실행
	const handleSearch = () => {
		currentPage = 1;
		fetchUsers();
	};

	// 검색어 초기화
	const handleReset = () => {
		searchKeyword = "";
		searchType = "ALL";
		currentPage = 1;
		fetchUsers();
	};

	// 페이지 변경
	const handlePageChange = (page) => {
		if (page < 1 || page > totalPages) return;
		currentPage = page;
		fetchUsers();
	};

	// 페이지 크기 변경
	const handlePageSizeChange = (event) => {
		pageSize = parseInt(event.target.value);
		currentPage = 1;
		fetchUsers();
	};

	// 상세 모달 닫기
	const closeDetailModal = () => {
		showDetailModal = false;
		selectedUser = null;
	};

	// 사용자 상태 표시
	const getUserStatus = (user) => {
		if (user.withdrawalAt) return "탈퇴";
		if (!user.isUse) return "비활성";
		return "활성";
	};

	// 사용자 상태 색상
	const getStatusColor = (user) => {
		if (user.withdrawalAt) return "text-danger";
		if (!user.isUse) return "text-warning";
		return "text-success";
	};

	// BMI 상태 색상
	const getBmiColor = (bmiInfo) => {
		switch (bmiInfo) {
			case "저체중":
				return "text-info";
			case "정상":
				return "text-success";
			case "과체중":
				return "text-warning";
			case "비만":
				return "text-danger";
			default:
				return "text-muted";
		}
	};

	// 키보드 이벤트 처리
	const handleKeyPress = (event) => {
		if (event.key === "Enter") {
			handleSearch();
		}
	};

	// 메일 발송 모달 제어
	const showHideMailModal = (to) => {
		if (showMailModal) {
			receiver = ['']
			showMailModal = false;
		} else {
			const isValidMail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to);
 			if(isValidMail) {
				receiver = [`${to}`];
				showMailModal = true;
			} else {
				alert("발신 가능한 이메일 아이디로 가입한 사용자가 아닙니다.")
			}
		}
	}

	onMount(() => {
		menu.set("사용자 관리");
		menuSub.set("사용자 조회");
		menuSubSub.set("");
		fetchUsers();
	});
</script>

<div class="card mb-4">
	<!-- 검색 영역 -->
	<div class="card-header">
		<div class="row align-items-center">
			<div class="col-md-6">
				<div class="d-flex align-items-center gap-2">
					<select class="form-select form-select-sm" style="width: 120px;" bind:value={searchType}>
						{#each searchOptions as option}
							<option value={option.value}>{option.text}</option>
						{/each}
					</select>
					<input
						type="text"
						class="form-control form-control-sm"
						placeholder="검색어를 입력하세요"
						bind:value={searchKeyword}
						on:keypress={handleKeyPress}
						style="width: 200px;"
					/>
					<button class="btn btn-primary btn-sm" on:click={handleSearch} disabled={isLoading}>
						<i class="bx bx-search"></i>
						검색
					</button>
					<button class="btn btn-outline-secondary btn-sm" on:click={handleReset}>
						<i class="bx bx-refresh"></i>
						초기화
					</button>
				</div>
			</div>
			<div class="col-md-6 text-end">
				<small class="text-muted">
					총 {totalItems.toLocaleString()}명의 사용자
				</small>
			</div>
		</div>
	</div>

	<div class="table-responsive text-nowrap">
		<TableToolbar
			{totalItems}
			{currentPage}
			{totalPages}
			{pageSize}
			pageSizes={PAGE_SIZES}
			onPageSizeChange={handlePageSizeChange}
			onReload={fetchUsers}
			onRegist={fnProcessRegist}
			onRegistServer={false}
		/>

		<div class="fade-container">
			<table class="table small">
				<thead>
					<tr style="text-align:center">
						<th width="80">No</th>
						<th width="*">이메일</th>
						<th width="120">로그인 타입</th>
						<th width="80">성별</th>
						<th width="80">나이</th>
						<th width="100">BMI</th>
						<th width="100">상태</th>
						<th width="120">가입일</th>
						<th width="100">관리</th>
					</tr>
				</thead>
				<tbody>
					{#if isLoading}
						<tr>
							<td colspan="9" class="text-center py-4">
								<div class="spinner-border spinner-border-sm me-2" role="status"></div>
								로딩 중...
							</td>
						</tr>
					{:else if filteredData.length === 0}
						<tr>
							<td colspan="9" class="text-center py-4 text-muted">조회된 사용자가 없습니다.</td>
						</tr>
					{:else}
						{#each filteredData as user, index}
							<tr style="text-align:center">
								<td>{user.id}</td>
								<td class="text-start" style="cursor: pointer;" on:click={()=>showHideMailModal(user.email)}>{user.email || "-"}</td>
								<td>
									<div class="d-flex justify-content-center gap-1">
										{#if user.isEmail}
											<span class="badge bg-primary">이메일</span>
										{/if}
										{#if user.isGoogle}
											<span class="badge bg-danger">구글</span>
										{/if}
										{#if user.isApple}
											<span class="badge bg-dark">애플</span>
										{/if}
										{#if user.isWeChat}
											<span class="badge bg-success">위챗</span>
										{/if}
										{#if !user.isEmail && !user.isGoogle && !user.isApple && !user.isWeChat}
											<span class="text-muted">-</span>
										{/if}
									</div>
								</td>
								<td>
									<i
										class={user.genderInfo === "01"
											? "bx bx-male text-primary"
											: user.genderInfo === "02"
												? "bx bx-female text-pink"
												: user.genderInfo === "03"
													? "bx bx-gender text-info"
													: "bx bx-question-mark text-secondary"}
										style="font-size: 2em;"
									></i>
								</td>

								<td>
									<span
										class={new Date().getFullYear() - parseInt(user.birthYear) < 20
											? "text-primary"
											: new Date().getFullYear() - parseInt(user.birthYear) < 30
												? "text-success"
												: new Date().getFullYear() - parseInt(user.birthYear) < 40
													? "text-warning"
													: new Date().getFullYear() - parseInt(user.birthYear) < 50
														? "text-info"
														: "text-dark"}
									>
										{isNaN(new Date().getFullYear() - parseInt(user.birthYear))
											? "알수없음"
											: new Date().getFullYear() - parseInt(user.birthYear) + 1}
									</span>
								</td>
								<td>
									<span class={getBmiColor(user.bmiInfo)}>
										{user.bmiAmount ? user.bmiAmount.toFixed(1) : "-"}
										{#if user.bmiInfo}
											<br />
											<small>({user.bmiInfo})</small>
										{/if}
									</span>
								</td>
								<!-- 상태 컬럼 토글로 변경 -->
								<td>
									<div class="form-check form-switch d-flex justify-content-center">
										<input
											class="form-check-input"
											type="checkbox"
											checked={user.isUse && !user.withdrawalAt}
											disabled
											readonly
											style={user.withdrawalAt ? "background: #ccc; border-color: #ccc;" : ""}
										/>
									</div>
								</td>
								<td>{Dates.defaultConvertFull(user.createdAt)}</td>
								<td>
									<button class="btn btn-sm btn-outline-primary" on:click={() => fetchUserDetail(user.id)}>
										<i class="bx bx-show"></i>
										상세
									</button>
								</td>
							</tr>
						{/each}
					{/if}
				</tbody>
			</table>
		</div>

		<Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
	</div>
</div>

<!-- 사용자 상세 모달 -->
{#if showDetailModal && selectedUser}
	<div class="modal fade show" style="display: block;" tabindex="-1" aria-modal="true" role="dialog" transition:fade={{ duration: 200 }}>
		<div class="modal-dialog modal-lg modal-dialog-centered">
			<div class="modal-content">
				<div class="modal-header">
					<h5 class="modal-title d-flex align-items-center">
						<i class="bx bx-user me-2"></i>
						사용자 상세 정보
					</h5>
					<button type="button" class="btn-close" on:click={closeDetailModal}></button>
				</div>
				<div class="modal-body">
					<div class="row">
						<!-- 기본 정보 -->
						<div class="col-md-6">
							<h6 class="border-bottom pb-2 mb-3">
								<i class="bx bx-info-circle me-1"></i>
								기본 정보
							</h6>
							<table class="table table-sm">
								<tr>
									<td class="fw-medium" style="width: 120px;">사용자 ID</td>
									<td>{selectedUser.idService || selectedUser.id}</td>
								</tr>
								<tr>
									<td class="fw-medium">닉네임</td>
									<td>{selectedUser.nickName}</td>
								</tr>
								<tr>
									<td class="fw-medium">이메일</td>
									<td>{selectedUser.email || "-"}</td>
								</tr>
								<tr>
									<td class="fw-medium">국가코드</td>
									<td>{selectedUser.countryCode}</td>
								</tr>
								<tr>
									<td class="fw-medium">출생년도</td>
									<td>{selectedUser.birthYear}년</td>
								</tr>
								<tr>
									<td class="fw-medium">성별</td>
									<td>
										{selectedUser.genderInfo === "01"
											? "남성"
											: selectedUser.genderInfo === "02"
												? "여성"
												: selectedUser.genderInfo === "03"
													? "기타"
													: "미설정"}
									</td>
								</tr>
								<tr>
									<td class="fw-medium">상태</td>
									<td>
										<span class={getStatusColor(selectedUser)}>
											{getUserStatus(selectedUser)}
										</span>
									</td>
								</tr>
							</table>
						</div>

						<!-- 신체 정보 -->
						<div class="col-md-6">
							<h6 class="border-bottom pb-2 mb-3">
								<i class="bx bx-body me-1"></i>
								신체 정보
							</h6>
							<table class="table table-sm">
								<tr>
									<td class="fw-medium" style="width: 120px;">키</td>
									<td>{selectedUser.heightInputInfo}{selectedUser.heightInputUnit}</td>
								</tr>
								<tr>
									<td class="fw-medium">몸무게</td>
									<td>{selectedUser.weightInputInfo}{selectedUser.weightInputUnit}</td>
								</tr>
								<tr>
									<td class="fw-medium">BMI</td>
									<td>
										<span class={getBmiColor(selectedUser.bmiInfo)}>
											{selectedUser.bmiAmount ? selectedUser.bmiAmount.toFixed(1) : "-"}
											{#if selectedUser.bmiInfo}
												({selectedUser.bmiInfo})
											{/if}
										</span>
									</td>
								</tr>
								<tr>
									<td class="fw-medium">질병 정보</td>
									<td>{selectedUser.diseaseInfo || "없음"}</td>
								</tr>
								<tr>
									<td class="fw-medium">활동 수준</td>
									<td>
										{#if (selectedUser.activityInfo = "01")}
											주1회
										{:else if (selectedUser.activityInfo = "02")}
											주2회
										{:else if (selectedUser.activityInfo = "03")}
											주3회
										{:else if (selectedUser.activityInfo = "04")}
											매일
										{:else if (selectedUser.activityInfo = "05")}
											불규칙적
										{:else if (selectedUser.activityInfo = "06")}
											규칙적
										{:else}
											-
										{/if}
									</td>
								</tr>
							</table>
						</div>

						<!-- 수면 정보 -->
						<div class="col-md-6">
							<h6 class="border-bottom pb-2 mb-3">
								<i class="bx bx-moon me-1"></i>
								수면 정보
							</h6>
							<table class="table table-sm">
								<tr>
									<td class="fw-medium">수면 시간</td>
									<td>
										{#if selectedUser.sleepTime}
											{parseInt(selectedUser.sleepTime.slice(0, 2), 10)}시간 {parseInt(selectedUser.sleepTime.slice(2), 10)}분
										{:else}
											-
										{/if}
									</td>
								</tr>
								<tr>
									<td class="fw-medium">수면 유형</td>
									<td>
										{#if (selectedUser.sleepType = "01")}
											규칙적
										{:else if (selectedUser.sleepType = "02")}
											가끔 규칙적
										{:else if (selectedUser.sleepType = "04")}
											불규칙적
										{:else}
											-
										{/if}
									</td>
								</tr>
							</table>
						</div>

						<!-- 시스템 정보 -->
						<div class="col-md-6">
							<h6 class="border-bottom pb-2 mb-3">
								<i class="bx bx-cog me-1"></i>
								시스템 정보
							</h6>
							<table class="table table-sm">
								<tr>
									<td class="fw-medium" style="width: 120px;">이메일 수신</td>
									<td>{selectedUser.isEmailReceiveAgree ? "동의" : "거부"}</td>
								</tr>
								<tr>
									<td class="fw-medium">로그인 실패</td>
									<td>{selectedUser.loginFailCount}회</td>
								</tr>
								<tr>
									<td class="fw-medium">테스트 계정</td>
									<td>{selectedUser.isTest ? "예" : "아니오"}</td>
								</tr>
								<tr>
									<td class="fw-medium">로그인 방식</td>
									<td>
										{#if selectedUser.isEmail}
											<span class="badge bg-primary me-1">이메일</span>
										{/if}
										{#if selectedUser.isGoogle}
											<span class="badge bg-danger me-1">구글</span>
										{/if}
										{#if selectedUser.isApple}
											<span class="badge bg-dark me-1">애플</span>
										{/if}
										{#if selectedUser.isWeChat}
											<span class="badge bg-success me-1">위챗</span>
										{/if}
										{#if !selectedUser.isEmail && !selectedUser.isGoogle && !selectedUser.isApple && !selectedUser.isWeChat}
											-
										{/if}
									</td>
								</tr>
								<tr>
									<td class="fw-medium">임시 비밀번호</td>
									<td>{selectedUser.isTempPassword ? "사용 중" : "미사용"}</td>
								</tr>
								<tr>
									<td class="fw-medium">가입일</td>
									<td>{Dates.defaultConvertFull(selectedUser.createdAt)}</td>
								</tr>
								<tr>
									<td class="fw-medium">수정일</td>
									<td>{Dates.defaultConvertFull(selectedUser.updatedAt)}</td>
								</tr>
								{#if selectedUser.withdrawalAt}
									<tr>
										<td class="fw-medium">탈퇴일</td>
										<td class="text-danger">{Dates.defaultConvertFull(selectedUser.withdrawalAt)}</td>
									</tr>
								{/if}
								{#if selectedUser.withdrawalReason}
									<tr>
										<td class="fw-medium">탈퇴 사유</td>
										<td class="text-danger">{selectedUser.withdrawalReason}</td>
									</tr>
								{/if}
							</table>
						</div>

						<!-- 디바이스 정보 -->
						<div class="col-md-6">
							<h6 class="border-bottom pb-2 mb-3">
								<i class="bx bx-mobile me-1"></i>
								디바이스 정보
							</h6>
							<table class="table table-sm">
								{#if selectedUser.iosVersion || selectedUser.iosModel}
									<tr>
										<td class="fw-medium" style="width: 120px;">iOS 기기</td>
										<td>{selectedUser.iosModel || "-"}</td>
									</tr>
									<tr>
										<td class="fw-medium">iOS 버전</td>
										<td>{selectedUser.iosVersion || "-"}</td>
									</tr>
									<tr>
										<td class="fw-medium">iOS 앱 버전</td>
										<td>{selectedUser.iosAppVersion || "-"} ({selectedUser.iosAppVersionCode || "-"})</td>
									</tr>
								{/if}
								{#if selectedUser.aosVersion || selectedUser.aosModel}
									<tr>
										<td class="fw-medium">Android 기기</td>
										<td>{selectedUser.aosModel || "-"}</td>
									</tr>
									<tr>
										<td class="fw-medium">Android 버전</td>
										<td>{selectedUser.aosVersion || "-"}</td>
									</tr>
									<tr>
										<td class="fw-medium">Android 앱 버전</td>
										<td>{selectedUser.aosAppVersion || "-"} ({selectedUser.aosAppVersionCode || "-"})</td>
									</tr>
								{/if}
								{#if !selectedUser.iosVersion && !selectedUser.iosModel && !selectedUser.aosVersion && !selectedUser.aosModel}
									<tr>
										<td colspan="2" class="text-center text-muted">등록된 디바이스 정보가 없습니다.</td>
									</tr>
								{/if}
							</table>
						</div>

						<!-- 계정 연동 정보 -->
						<div class="col-md-6">
							<h6 class="border-bottom pb-2 mb-3">
								<i class="bx bx-link me-1"></i>
								계정 연동 정보
							</h6>
							<table class="table table-sm">
								<tr>
									<td class="fw-medium" style="width: 120px;">계정 수신 동의</td>
									<td>{selectedUser.isEmailReceiveAgreeAccount ? "동의" : "거부"}</td>
								</tr>
								{#if selectedUser.emailAppendGoogleAt}
									<tr>
										<td class="fw-medium">구글 연동일</td>
										<td>{Dates.defaultConvertFull(selectedUser.emailAppendGoogleAt)}</td>
									</tr>
								{/if}
								{#if selectedUser.emailReleaseGoogleAt}
									<tr>
										<td class="fw-medium">구글 연동 해제일</td>
										<td class="text-warning">{Dates.defaultConvertFull(selectedUser.emailReleaseGoogleAt)}</td>
									</tr>
								{/if}
								{#if selectedUser.emailAppendAppleAt}
									<tr>
										<td class="fw-medium">애플 연동일</td>
										<td>{Dates.defaultConvertFull(selectedUser.emailAppendAppleAt)}</td>
									</tr>
								{/if}
								{#if selectedUser.emailReleaseAppleAt}
									<tr>
										<td class="fw-medium">애플 연동 해제일</td>
										<td class="text-warning">{Dates.defaultConvertFull(selectedUser.emailReleaseAppleAt)}</td>
									</tr>
								{/if}
								{#if selectedUser.tempPasswordExpAt}
									<tr>
										<td class="fw-medium">임시 비밀번호 만료</td>
										<td class="text-danger">{Dates.defaultConvertFull(selectedUser.tempPasswordExpAt)}</td>
									</tr>
								{/if}
								{#if selectedUser.servicePrivacyEmailAt}
									<tr>
										<td class="fw-medium">개인정보 이메일</td>
										<td>{Dates.defaultConvertFull(selectedUser.servicePrivacyEmailAt)}</td>
									</tr>
								{/if}
							</table>
						</div>
					</div>

					{#if selectedUser.imagePath}
						<div class="row mt-3">
							<div class="col-12">
								<h6 class="border-bottom pb-2 mb-3">
									<i class="bx bx-image me-1"></i>
									프로필 이미지
								</h6>
								<img
									src={selectedUser.imagePath}
									alt="프로필 이미지"
									class="img-thumbnail"
									style="max-width: 200px; max-height: 200px;"
									on:error={(e) => (e.target.style.display = "none")}
								/>
							</div>
						</div>
					{/if}
				</div>
				<div class="modal-footer">
					<button type="button" class="btn btn-secondary" on:click={closeDetailModal}>닫기</button>
				</div>
			</div>
		</div>
	</div>
{/if}

<!-- 모달 영역 -->
{#if showRegistModal}
	<div class="modal-overlay">
		<div
			class="modal fade {showRegistModal ? 'show' : ''}"
			style="display: block;"
			tabindex="-1"
			aria-modal="true"
			role="dialog"
			transition:fade={{ duration: 200 }}
		>
			<div class="modal-dialog modal-dialog-centered">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">사용자 등록</h5>
						<button type="button" class="btn-close" on:click={closeRegistModal} disabled={isLoading} />
					</div>
					<div class="modal-body">
						<div class="mb-3">
							<label class="form-label" for="modalEmail">이메일 입력</label>
							<input id="modalEmail" class="form-control" placeholder="이메일을 입력하세요" bind:value={editItem.email} />
						</div>
						<div class="mb-3">
							<label class="form-label" for="modalPassword">PW 입력</label>
							<input id="modalPassword" class="form-control" placeholder="PW를 입력하세요" bind:value={editItem.password} />
						</div>
						<!-- 등록/수정 모달에 isUse 토글 추가 (editItem이 있을 때만) -->
						{#if editItem}
							<div class="form-check form-switch mb-3">
								<input class="form-check-input" type="checkbox" id="isUse-edit" bind:checked={editItem.isUse} />
								<label class="form-check-label" for="isUse-edit">사용 여부</label>
							</div>
						{/if}
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" on:click={closeRegistModal} disabled={isLoading}>취소</button>
						<button type="button" class="btn btn-primary" on:click={handleSave} disabled={isLoading}>
							{#if isLoading}
								<span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
								등록 중...
							{:else}
								등록
							{/if}
						</button>
					</div>
				</div>
			</div>
		</div>
		<div class="modal-backdrop fade show" />
	</div>
{/if}

<!--메일 발송 모달 영역 -->
<SendMailModal 
	handleShowHideModal = {showHideMailModal}
	showModal = {showMailModal}
	to = {receiver}/>
<style>
	.fade-container {
		opacity: 1;
		transition: opacity 0.15s linear;
	}

	.table td {
		vertical-align: middle;
	}

	.modal {
		background-color: rgba(0, 0, 0, 0.5);
	}

	.table-sm td {
		padding: 0.25rem 0.5rem;
		border-top: 1px solid #dee2e6;
	}

	.table-sm td:first-child {
		background-color: #f8f9fa;
		font-weight: 500;
	}
</style>
